import { useEffect, useMemo, useRef, useState } from "react";

import { cn } from "../lib/utils";

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return reduced;
}

export function AnimatedCounter({
  value,
  suffix,
  durationMs = 900,
  className,
}) {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [display, setDisplay] = useState(0);
  const startedRef = useRef(false);
  const elRef = useRef(null);

  const formatted = useMemo(() => {
    const n = Math.round(display);
    return `${n.toLocaleString()}${suffix ?? ""}`;
  }, [display, suffix]);

  useEffect(() => {
    if (!elRef.current) return;

    const el = elRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        if (startedRef.current) return;
        startedRef.current = true;

        if (prefersReducedMotion) {
          setDisplay(value);
          return;
        }

        const start = performance.now();
        const from = 0;
        const to = value;

        const tick = (now) => {
          const t = Math.min(1, (now - start) / durationMs);
          const eased = 1 - Math.pow(1 - t, 3);
          setDisplay(from + (to - from) * eased);
          if (t < 1) requestAnimationFrame(tick);
        };

        requestAnimationFrame(tick);
      },
      { threshold: 0.2, rootMargin: "0px 0px -10% 0px" },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [durationMs, prefersReducedMotion, value]);

  return (
    <span ref={elRef} className={cn(className)}>
      {formatted}
    </span>
  );
}
